import React, { useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
  Alert,
  Space,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_API_URL } from "../../../utils/consts";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";
import {
  CompanyContract,
  CompanyContractType,
} from "../../../utils/api/companyContractsAPI";
import SettingsHeader from "../Components/SettingsHeader/SettingsHeader";

const { Text } = Typography;

const CONTRACT_TYPES: { value: CompanyContractType; labelKey: string }[] = [
  { value: "GARDEN", labelKey: "Garden / Terrace" },
  { value: "PEST_CONTROL", labelKey: "Pest Control (Deratization)" },
  { value: "FIRE_SAFETY", labelKey: "Fire Safety" },
  { value: "CLEANING", labelKey: "Cleaning Service" },
  { value: "SECURITY", labelKey: "Security" },
  { value: "MAINTENANCE", labelKey: "Maintenance" },
  { value: "WASTE_DISPOSAL", labelKey: "Waste Disposal" },
  { value: "ELEVATOR", labelKey: "Elevator Service" },
  { value: "HVAC", labelKey: "HVAC / Air Conditioning" },
  { value: "LEASE", labelKey: "Lease" },
  { value: "OTHER", labelKey: "Other" },
];

const CompanyContractDetails: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const userData = useSelector((state: any) => state.userData.user);

  const [locations, setLocations] = useState<{ _id: string; name: string }[]>(
    [],
  );
  const [durationDays, setDurationDays] = useState<number | null>(null);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const isNew = params.id === "new";
  const clientId = userData?.clientId;

  useEffect(() => {
    if (!clientId) return;
    axios
      .get(`${BASE_API_URL}/${clientId}/locations`)
      .then((res) => setLocations((res.data || []).filter(isLocationActive)))
      .catch((err) => console.error(err));
  }, [clientId]);

  useEffect(() => {
    if (isNew || !params.id) return;
    axios
      .get<CompanyContract>(`${BASE_API_URL}/company-contract/${params.id}`)
      .then((res) => {
        const c = res.data;
        const locId =
          typeof c.locationId === "object" ? c.locationId._id : c.locationId;
        form.setFieldsValue({
          locationId: locId,
          name: c.name,
          type: c.type,
          contractNumber: c.contractNumber,
          supplier: c.supplier,
          startDate: c.startDate ? dayjs(c.startDate) : undefined,
          endDate: c.endDate ? dayjs(c.endDate) : undefined,
          value: c.value,
          currency: c.currency ?? "EUR",
          notes: c.notes,
        });
        if (c.startDate && c.endDate) {
          const dur = dayjs(c.endDate).diff(dayjs(c.startDate), "day");
          setDurationDays(dur);
          setDaysUntilExpiry(dayjs(c.endDate).diff(dayjs(), "day"));
        }
      })
      .catch((err) => console.error(err));
  }, [params.id, isNew, form]);

  const recalcDuration = () => {
    const start = form.getFieldValue("startDate");
    const end = form.getFieldValue("endDate");
    if (start && end) {
      setDurationDays(dayjs(end).diff(dayjs(start), "day"));
      setDaysUntilExpiry(dayjs(end).diff(dayjs(), "day"));
    } else {
      setDurationDays(null);
      setDaysUntilExpiry(null);
    }
  };

  const onFinish = async (values: any) => {
    if (!clientId) return;
    setLoading(true);
    const payload = {
      clientId,
      locationId: values.locationId,
      name: values.name,
      type: values.type,
      contractNumber: values.contractNumber,
      supplier: values.supplier,
      startDate: values.startDate
        ? dayjs(values.startDate).format("YYYY-MM-DD")
        : undefined,
      endDate: values.endDate
        ? dayjs(values.endDate).format("YYYY-MM-DD")
        : undefined,
      value: values.value,
      currency: values.currency ?? "EUR",
      notes: values.notes,
    };

    const req = isNew
      ? axios.post(`${BASE_API_URL}/company-contract/create`, payload)
      : axios.put(`${BASE_API_URL}/company-contract/${params.id}`, payload);

    req
      .then(() => navigate("/settings?tab=CompanyContracts"))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const expiryAlert = () => {
    if (daysUntilExpiry === null) return null;
    if (daysUntilExpiry < 0)
      return (
        <Alert
          type="error"
          showIcon
          message={t("Expired {{count}} days ago", {
            count: Math.abs(daysUntilExpiry),
          })}
          style={{ marginBottom: 16 }}
        />
      );
    if (daysUntilExpiry <= 7)
      return (
        <Alert
          type="error"
          showIcon
          message={t("Expires in {{count}} days", { count: daysUntilExpiry })}
          style={{ marginBottom: 16 }}
        />
      );
    if (daysUntilExpiry <= 30)
      return (
        <Alert
          type="warning"
          showIcon
          message={t("Expires in {{count}} days", { count: daysUntilExpiry })}
          style={{ marginBottom: 16 }}
        />
      );
    return null;
  };

  return (
    <>
      <SettingsHeader
        title={isNew ? t("New Company Contract") : t("Edit Company Contract")}
        onBack={() => navigate("/settings?tab=CompanyContracts")}
      />
      <div className="settingsForm">
        {expiryAlert()}
        <Form
          form={form}
          name="companyContract"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label={t("Location")}
            name="locationId"
            rules={[{ required: true, message: t("Please select a location") }]}
          >
            <Select
              placeholder={t("Select location")}
              options={locations.map((l) => ({ value: l._id, label: l.name }))}
            />
          </Form.Item>

          <Form.Item
            label={t("Contract Name")}
            name="name"
            rules={[
              { required: true, message: t("Please enter contract name") },
            ]}
          >
            <Input placeholder={t("e.g. Garden Maintenance 2025")} />
          </Form.Item>

          <Form.Item
            label={t("Contract Type")}
            name="type"
            rules={[
              { required: true, message: t("Please select contract type") },
            ]}
          >
            <Select
              placeholder={t("Select contract type")}
              options={CONTRACT_TYPES.map((ct) => ({
                value: ct.value,
                label: t(ct.labelKey),
              }))}
            />
          </Form.Item>

          <Form.Item label={t("Contract Number")} name="contractNumber">
            <Input placeholder={t("e.g. 2025/001")} />
          </Form.Item>

          <Form.Item label={t("Supplier / Provider")} name="supplier">
            <Input placeholder={t("Name of the company or service provider")} />
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item
              label={t("Start Date")}
              name="startDate"
              rules={[
                { required: true, message: t("Please select start date") },
              ]}
              style={{ flex: 1 }}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD.MM.YYYY"
                onChange={recalcDuration}
              />
            </Form.Item>
            <Form.Item
              label={t("End Date")}
              name="endDate"
              rules={[{ required: true, message: t("Please select end date") }]}
              style={{ flex: 1 }}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD.MM.YYYY"
                onChange={recalcDuration}
              />
            </Form.Item>
          </div>

          {durationDays !== null && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                {t("Duration")}:{" "}
                <strong>
                  {durationDays} {t("days")}
                </strong>
                {durationDays >= 365 && (
                  <span>
                    {" "}
                    ({Math.floor(durationDays / 365)} {t("yr")}
                    {durationDays % 365 > 0
                      ? ` ${durationDays % 365} ${t("days")}`
                      : ""}
                    )
                  </span>
                )}
              </Text>
            </div>
          )}

          <Form.Item label={t("Contract Value")}>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="value" noStyle>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                />
              </Form.Item>
              <Form.Item
                name="currency"
                noStyle
                initialValue={userData?.clientId?.settings?.currency ?? "EUR"}
              >
                <Select style={{ width: 90 }}>
                  <Select.Option value="EUR">EUR</Select.Option>
                  <Select.Option value="RSD">RSD</Select.Option>
                  <Select.Option value="BAM">BAM</Select.Option>
                </Select>
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          <Form.Item label={t("Notes")} name="notes">
            <Input.TextArea
              rows={3}
              placeholder={t("Additional notes or remarks")}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("Save")}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default CompanyContractDetails;
